import { ItemListView, ValueOrVariableType } from "@app/types";
import ReactDOM from "react-dom";
import BaseComponent from "./BaseComponent";
import Button from "./Button";
import Submenu from "./Submenu";
import ArgumentSubCon from "./submenu_contents/ArgumentSubCon";
import LocalIntentSubCon from "./submenu_contents/LocalIntentSubCon";
import KeyValueSubCon from "./submenu_contents/KeyValueSubCon";
import KeyValue from "./KeyValue";
import CustomSchemaDialog from "../widgets/dialogs/CustomSchemaDialog";
import CustomCSVDialog from "../widgets/dialogs/CustomCSVDialog";
import * as Papa from "papaparse";
import RouterArgumentSubCon from "./submenu_contents/RouterArgumentSubCon";
import SetValSubCon from "./submenu_contents/SetValSubCon";
import map from "lodash/map";
import CustomFunctionDialog from "../widgets/dialogs/CustomFunctionDialog";
import ReturnSubCon from "./submenu_contents/ReturnSubCon";
import RouterReturnSubCon from "./submenu_contents/RouterReturnSubCon";
import ModuleReturnSubCon from "./submenu_contents/ModuleReturnSubCon";
import DynamicVariableSubCon from "./submenu_contents/DynamicVariableSubCon";
import CustomHeaderSubCon from "./submenu_contents/CustomHeaderSubCon";
import UsedInDialog from "../widgets/dialogs/UsedInDialog";
import { speakAsOptions } from "../const";
import { updateLastChange } from "../utilities";
import { digitPlaceholderSearch } from "../const";

/**
 * @template T
 * @typedef {Object} OldItemListContext
 * @property {(key: string) => T[]} getConfig
 * @property {Record<string, unknown>} props
 * @property {(key: string, value: T[]) => void} setConfig
 */

/**
 * @template T
 * @typedef {Object} OldItemListProps
 * @property {OldItemListContext<T>} context
 * @property {string} label
 * @property {string} valKey
 * @property {ItemListView} view
 * @property {T[]} value
 */

/**
 * @template T
 * @extends BaseComponent<OldItemListProps<T>>
 */
export class OldItemList extends BaseComponent {
  /**
   * Display a list of items with customizable item view
   * @deprecated Use ItemList from form_components/item-list instead
   * @module OldItemList
   * @param {Object} props React props
   */
  constructor(props) {
    super(props);
    this.state = {
      label: this.props.label ? this.props.label : "",
      items: this.props.value
        ? JSON.parse(JSON.stringify(this.props.value))
        : []
    };
    this.eventHandler = this.handleEvent.bind(this);
  }

  handleEvent(event) {
    if (event.type === "ev-property-item-list-refresh") {
      this.setState({
        items: JSON.parse(JSON.stringify(this.getContextValue()))
      });
    }
  }

  componentDidUpdate() {
    if (this.props.broadcast) {
      document.dispatchEvent(new CustomEvent("ev-item-list-update"));
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      "ev-property-item-list-refresh",
      this.eventHandler
    );
  }

  /**
   * Add to list
   * @param {*} itemData
   */
  add(itemData) {

    // DEVDES2-1487 seen in adding outputs to module reference nodes 
    if(typeof itemData === 'object' && !itemData.argumentName){
      itemData.argumentName = itemData.varName;
    }

    this.setState(prevState => {
      const items = [...prevState.items, itemData];
      if (itemData.script !== undefined) {
        var idx = items.findIndex(element => element.id === itemData.id);
        items[idx].createdTime = window.apm.timestamp;
      }
      if (this.props.context.props.nodeTypeName === "Module Reference") {
        const scope = itemData.argumentName.split(".")[0];
        const varName = itemData.argumentName.split(".")[1];
        if (scope !== "mod" && !varName) {
          itemData.argumentName = "mod." + itemData.argumentName;
        }
      }
      this.updateContext(items);
      window.apm.setSerializedChanged();
      return { items: items };
    });
  }

  /**
   * Remove item at index
   * @param {number} index
   */
  remove(index) {
    //LangSubCon should not be able to remove English, which is always index 0
    if (this.props.valKey === "languages") {
      if (index === 0) return;
    }
    // TODO: 
    window.localStorage.removeItem('removedItem');

    this.setState(prevState => {
      const items = prevState.items;
      const removedItem = prevState.items.splice(index, 1)[0];
      window.localStorage.setItem("removedItem", JSON.stringify(removedItem));
      this.updateContext(items);
      return { items: items };
    });
  }
  /**
   * Update item at index
   * @param {*} itemData
   * @param {number} index
   */
  update(itemData, index) {
    this.setState(prevState => {
      const items = prevState.items;
      if (
        this.props.context.type === "module" &&
        this.props.label === "Parameters"
      ) {
        items.forEach(item => {
          item.value = itemData.value;
        });
      } else {
        if (index && items[index].script !== undefined) {
          itemData.lastSavedChange = items[index].lastSavedChange;
          itemData.createdTime = items[index].createdTime;
          updateLastChange(itemData);
        }
        items[index] = itemData;
      }

      this.updateContext(items);
      return { items: items };
    });
  }

  getHeader(){
    const header =  this.props.headerItems.map(header => {
       return <div key={header} style={{flex: 1, fontWeight: "800"}}>{header}</div>;
    });
    header.push(<div key="empty" style={{width: "16px", padding: "4px"}}></div>)
    return header;
  }

  /**
   * General renderer for item list
   * @param {Array} itemList
   * @param {Object} submenu Submenu component
   * @param {Object} menuParams Submenu parameters
   * @returns {XML}
   */
  renderList(itemList, submenu, menuParams = null) {
    const callback = this.props.callback || '__submenu';
    return (
      <div
        className={"form-component-container"}
        style={{ position: "relative" }}
      >
        {(() => {
          if (
            this.props.view === ItemListView.variableReference ||
            this.props.updateOnly ||
            this.props.readOnly
          ) {
            return <label>{this.state.label}</label>;
          }
          if (
            this.props.label === "Inputs" &&
            this.props.context.props &&
            this.props.context.props.isCommonModule &&
            this.props.context.props.isRootModule
          ) {
            return <label>{this.state.label}</label>;
          }
          return (
            <Button
              label="+"
              type="square"
              desc={this.state.label}
              callback={callback}
              context={this}
              submenu={submenu}
              tooltip={this.props.tooltip}
              menuParams={menuParams}
            />
          );
        })()}
        {(() => {
          if (itemList.length > 0) {
            return (
              <div className={`form-list-wrapper type-${this.props.view}`} style={{ position: "relative" }}>
                
                {this.props.headerItems &&
                  <div style={{display: "flex", paddingLeft: "4px", paddingRight: "12px", marginTop: "12px", marginBottom: "5px"}}>
                    {this.getHeader()}
                  </div>
                }
                
                <ul className={"form-list"}>{itemList}</ul>
              </div>
            );
          }
        })()}
      </div>
    );
  }

  /**
   * React render
   * @returns {XML}
   */
  render() {
    const itemList = [];
    if (this.props.view === ItemListView.argument) {
      this.state.items.forEach((item, i) =>{
        itemList.push(
          <ArgumentItem
            data={item}
            context={this}
            content={ArgumentSubCon}
            varIdx={i}
            key={i}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, ArgumentSubCon);
    }

    if (this.props.view === ItemListView.customHeader) {
      this.state.items.forEach((item, i) => {
        itemList.push(
          <HeaderItem
            data={item}
            context={this}
            content={CustomHeaderSubCon}
            varIdx={i}
            key={i}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, CustomHeaderSubCon);
    }

    if (this.props.view === ItemListView.return) {
      this.state.items.forEach((item, i) =>{
        itemList.push(
          <ReturnItem
            data={item}
            context={this}
            content={ReturnSubCon}
            varIdx={i}
            key={i}
            readOnly={this.props.readOnly}
          />
        );
      })
      return this.renderList(itemList, ReturnSubCon);
    }

    if (this.props.view === ItemListView.routerArgument) {
      const menuParams = this.props.pairKeyView;
      this.state.items.forEach((item, i) => {
        itemList.push(
          <RouterArgumentItem
            data={item}
            context={this}
            content={RouterArgumentSubCon}
            varIdx={i}
            key={i}
            menuParams={menuParams}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, RouterArgumentSubCon, menuParams);
    }
    if (this.props.view === ItemListView.routerReturn) {
      const menuParams = this.props.pairKeyView;
      this.state.items.forEach((item, i) => {
        itemList.push(
          <RouterReturnItem
            data={item}
            context={this}
            content={RouterReturnSubCon}
            varIdx={i}
            key={i}
            menuParams={menuParams}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, RouterReturnSubCon, menuParams);
    }

    if (this.props.view === ItemListView.moduleInputs) {
      if(this.props.context.id === this.props.context.props.parentComponentID){
        return this.renderList(itemList);
      }
      
    }

    if (this.props.view === ItemListView.moduleReturn) {
      const menuParams = this.props.pairKeyView;
      this.state.items.forEach((item, i) => {
        itemList.push(
          <ModuleReturnItem
            data={item}
            context={this}
            content={ModuleReturnSubCon}
            varIdx={i}
            key={i}
            menuParams={menuParams}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, ModuleReturnSubCon, menuParams);
    }
    if (this.props.view === ItemListView.functionArgument) {
      this.state.items.forEach((item, i) => {
        itemList.push(
          <FunctionArgumentItem
            data={item}
            context={this}
            content={ArgumentSubCon}
            varIdx={i}
            key={i}
            updateOnly={this.props.updateOnly}
          />
        );
      });
      return this.renderList(itemList, ArgumentSubCon);
    }
    if (this.props.view === ItemListView.variableReference) {
      document.addEventListener(
        "ev-property-item-list-refresh",
        this.eventHandler
      );
      const sorted = this.state.items.sort((a, b) => {
        return a.argumentName.localeCompare(b.argumentName);
      });
      sorted.forEach((item, i) => {
        itemList.push(
          <VariableReferenceItem
            data={item}
            context={this}
            varIdx={i}
            key={i}
          />
        );
      });
      return this.renderList(itemList, null);
    }
    if (this.props.view === ItemListView.dynamicVariableReference) {
      document.addEventListener(
        "ev-property-item-list-refresh",
        this.eventHandler
      );
      const sorted2 = this.state.items.sort((a, b) => {
        return a.argumentName.localeCompare(b.argumentName);
      });
      sorted2.forEach((item, i) => {
        if (!item.varName || item.varName.match(digitPlaceholderSearch)) {
          return;
        }
        itemList.push(
          <DynamicVariableReferenceItem
            data={item}
            context={this}
            varIdx={i}
            key={i}
          />
        );
      });
      return this.renderList(itemList, null);
    }
    if (this.props.view === ItemListView.localIntent) {
      document.addEventListener(
        "ev-property-item-list-refresh",
        this.eventHandler
      );
      this.state.items.forEach((item, i) => {
        itemList.push(
          <LocalIntentItem
            data={item}
            context={this}
            content={LocalIntentSubCon}
            varIdx={i}
            key={i}
            readOnly={this.props.readOnly}
          />
        );
      });
      return this.renderList(itemList, LocalIntentSubCon);
    }
    if (this.props.view === ItemListView.keyValue) {
      const params = (this.props.context.props.config && this.props.context.props.config.parameters) ? [] : this.state.items;
      var allowDelete = this.props.allowDelete;
      if (this.props.context.props.config && this.props.context.props.config.parameters) {
        allowDelete = false;

        this.props.context.props.config.parameters.forEach((param) => {
          if (this.props.context.type === "module" && param.key) {
            params.push({
              key: param.key,
              value: param.value
            });
          } else if (param.varName) {
            params.push({
              key: param.varName,
              value: param.value
            });
          } else {
            params.push(param);
          }
        });
      }
      params.forEach((param, i) => {
        itemList.push(
          <KeyValueItem
            data={param}
            context={this}
            varIdx={i}
            key={i}
            pairKeyView={this.props.pairKeyView}
            pairValueView={this.props.pairValueView}
            updateOnly={this.props.updateOnly}
            icon={this.props.icon}
            allowDelete={allowDelete}
          />
        );
      });

      if (this.props.menuType === "setVal") {
        return (
          <div
            className={"form-component-container"}
            style={{ position: "relative" }}
          >
            {(() => {
              return (
                <Button
                  label="+"
                  type="square"
                  desc={this.state.label}
                  callback={"__submenu"}
                  context={this}
                  submenu={SetValSubCon}
                  menuParams={{ options: this.props.pairValueView.options }}
                />
              );
            })()}
            {(() => {
              if (itemList.length > 0) {
                return (
                  <div style={{ position: "relative" }}>
                    <ul className={"form-list"}>{itemList}</ul>
                  </div>
                );
              }
            })()}
          </div>
        );
      }
      if (this.props.pairKeyView !== "parameters") {
        var menuParams = this.props.pairKeyView
          ? {
            keyText: this.props.pairKeyView.hint,
            valText: this.props.pairValueView.hint
          }
          : {};
      }

      if (this.props.hasOwnProperty("setDefault")) {
        menuParams = {
          keyText: this.getPropLabel("key"),
          valText: this.getPropLabel("default_value")
        };
      }
      return this.renderList(itemList, KeyValueSubCon, menuParams);
    }
    if (this.props.view === ItemListView.schema) {
      this.state.items.forEach((item, i) => {
        itemList.push(
          <SchemaItem
            data={item}
            context={this}
            varIdx={i}
            key={i}
          />
        );
      });
      return (
        <div
          className={"form-component-container"}
          style={{ position: "relative" }}
        >
          {(() => {
            return (
              <Button
                label="+"
                type="square"
                desc={this.state.label}
                callback={() => {
                  window.apm.popup(
                    window.l.settings.schema,
                    '<div class="custom_script_container"></div>',
                    '<button class="form-button normal" onclick="w2popup.close();">' +
                    `  ${window.l.popup.close}` +
                    "</button> " +
                    '<button class="form-button add-button">' +
                    `  ${window.l.popup.add}` +
                    "</button> ",
                    [
                      ".custom_script_container",

                      () => {
                        ReactDOM.render(
                          <CustomSchemaDialog context={this} new={true} />,
                          $(".custom_script_container").get(0)
                        );
                        $(".w2ui-popup")
                          .find(".add-button")
                          .on("click", function () {
                            document.dispatchEvent(
                              new CustomEvent("ev-dialog-add")
                            );
                          });
                      
                      }
                    ]
                    
                  );
                }}
                context={this}
              />
            );
          })()}
          {(() => {
            if (itemList.length > 0) {
              return (
                <div style={{ position: "relative" }}>
                  <ul className={"form-list"}>{itemList}</ul>
                </div>
              );
            }
            // return (<em className="greyout">No variable set</em>);
          })()}
        </div>
      );
    }
    if (this.props.view === ItemListView.function) {
      this.state.items.forEach((item, i) => {
        itemList.push(
          <FunctionItem
            data={item}
            context={this}
            varIdx={i}
            key={i}
          />
        );
      });
      return (
        <div
          className={"form-component-container"}
          style={{ position: "relative" }}
        >
          {(() => {
            return (
              <Button
                label="+"
                type="square"
                desc={this.state.label}
                callback={() => {
                  window.apm.popup(
                    window.l.settings.function,
                    '<div class="custom_script_container"></div>',
                    '<button class="form-button normal" onclick="w2popup.close();">' +
                    `  ${window.l.popup.close}` +
                    "</button> " +
                    '<button class="form-button add-button">' +
                    `  ${window.l.popup.add_function}` +
                    "</button> ",
                    [
                      ".custom_script_container",
                      () => {
                          ReactDOM.render(
                            <CustomFunctionDialog onSave={(functionData) => this.add(functionData)} />,
                            $(".custom_script_container").get(0)
                          );
                          $(".w2ui-popup")
                            .find(".add-button")
                            .on("click", function () {
                              document.dispatchEvent(
                                new CustomEvent("ev-dialog-add")
                              );
                            });
                      }
                    ]
                  );
                }}
                context={this}
              />
            );
          })()}
          {(() => {
            if (itemList.length > 0) {
              return (
                <div style={{ position: "relative" }}>
                  <ul className={"form-list"}>{itemList}</ul>
                </div>
              );
            }
          })()}
        </div>
      );
    }
    if (this.props.view === ItemListView.csv) {
      this.state.items.forEach((item, i) => {
        itemList.push(
          <CSVItem
            data={item}
            context={this}
            varIdx={i}
            key={i}
          />
        );
      });

      return (
        <div
          className={"form-component-container"}
          style={{ position: "relative" }}
        >
          {(() => {
            return (
              <Button
                label="+"
                type="square"
                desc={this.state.label}
                callback={() => {
                  window.apm.popup(
                    window.l.settings.csv_data,
                    '<div class="custom_script_container"></div>',
                    '<button class="form-button normal" onclick="w2popup.close();">' +
                    `  ${window.l.popup.close}` +
                    "</button> " +
                    '<button class="form-button add-button">' +
                    `  ${window.l.popup.upload_csv}` +
                    "</button> ",
                    [
                      ".custom_script_container",
                      () => {
                        ReactDOM.render(
                          <CustomCSVDialog context={this} new={true} />,
                          $(".custom_script_container").get(0)
                        );
                        $(".w2ui-popup")
                          .find(".add-button")
                          .on("click", function () {
                            document.dispatchEvent(
                              new CustomEvent("ev-dialog-add")
                            );
                          });
                      
                      }

                    ]
                    
                  );
                }}
                context={this}
              />
            );
          })()}
          {(() => {
            if (itemList.length > 0) {
              return (
                <div style={{ position: "relative" }}>
                  <ul className={"form-list"}>{itemList}</ul>
                </div>
              );
            }
            // return (<em className="greyout">No variable set</em>);
          })()}
        </div>
      );
    }
    return <span />;
  }
}

export default OldItemList;

/**
 * Base class of list item
 * @class ListItem
 */
class ListItem extends BaseComponent {
  /**
   * @param {Object} props React props
   */
  constructor(props) {
    super(props);
    this.state = {
      showJsx: false
    };
  }

  /**
   * Get reference of list item itself
   * @returns {jQuery}
   */
  getDomSelf() {
    return $(ReactDOM.findDOMNode(this)).find(".form-list-item");
  }

  /**
   * Hide attached submenu
   */
  removeSubmenu() {
    this.setState({ showJsx: false });
  }
}

/**
 * Custom list item views
 */

/**
 * @class ArgumentItem
 */
class ArgumentItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                    <div className={"subtext"}>
                      {(() => {
                        if (this.props.data.type === ValueOrVariableType.variable) {
                          return (
                            <span>
                              <i className={"fe icon-hash"} />
                              {this.props.data.varName}
                            </span>
                          );
                        }
                        if (this.props.data.type === ValueOrVariableType.value) {
                          return (
                            <span>
                              <i className={"fe icon-sliders"} />
                              {this.props.data.value}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class HeaderItem
 */
 class HeaderItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                    <div className={"subtext"}>
                      {(() => {
                        if (
                          this.props.data.type === "variable"
                        ) {
                          return (
                            <span>
                              <i className={"fe icon-hash"} />
                              {this.props.data.varName}
                            </span>
                          );
                        }
                        if (this.props.data.type === "value") {
                          return (
                            <span>
                              <i className={"fe icon-sliders"} />
                              {this.props.data.value}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class ReturnItem
 */
class ReturnItem extends ListItem {
  render() {
    const idx = window.apm.knowledgeBase.getVariableTypeIndexFromValue(
      this.props.data.type
    );
    var optionName = "Undefined";
    if (idx >= 0) {
      optionName = window.apm.knowledgeBase.getVariableTypes()[idx].name;
    }
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>To: {this.props.data.varName}</div>
                    <div className={"subtext"}>{optionName}</div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class RouterArgumentItem
 */
class RouterArgumentItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td style={{ width: "18px" }}>{this.props.varIdx}.</td>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                    <div className={"subtext"}>
                      {(() => {
                        if (this.props.data.type === ValueOrVariableType.variable) {
                          return (
                            <span>
                              <i className={"fe icon-hash"} />
                              {this.props.data.varName}
                            </span>
                          );
                        }
                        if (this.props.data.type === ValueOrVariableType.value) {
                          return (
                            <span>
                              <i className={"fe icon-sliders"} />
                              {this.props.data.value}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  params={this.props.menuParams}
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class RouterReturnItem
 */
class RouterReturnItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.varName}</div>
                    <div className={"subtext"}>
                      {this.props.data.argumentName}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  params={this.props.menuParams}
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class ModuleReturnItem
 */
class ModuleReturnItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>To: {this.props.data.varName}</div>
                    <div className={"subtext"}>
                      From: {this.props.data.argumentName}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  params={this.props.menuParams}
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  readOnly={this.props.readOnly}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class FunctionArgumentItem
 */
class FunctionArgumentItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              if (!this.props.readOnly) {
                this.setState({ showJsx: true });
              }
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                    <div className={"subtext"}>
                      {(() => {
                        if (this.props.data.type === ValueOrVariableType.variable) {
                          return (
                            <span>
                              <i className={"fe icon-hash"} />
                              {this.props.data.varName}
                            </span>
                          );
                        }
                        if (this.props.data.type === ValueOrVariableType.value) {
                          return (
                            <span>
                              <i className={"fe icon-sliders"} />
                              {this.props.data.value}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  params={{ updateOnly: this.props.updateOnly }}
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{
                    varIdx: this.props.varIdx,
                    variable: this.props.data
                  }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class VariableReferenceItem
 */
class VariableReferenceItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
        </div>
      </li>
    );
  }
}

/**
 * @class DynamicVariableReferenceItem
 */
class DynamicVariableReferenceItem extends ListItem {
  render() {
    return (
      <li>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
            className={"form-list-item item-button"}
            onClick={() => {
              this.setState({ showJsx: true });
            }}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{this.props.data.argumentName}</div>
                    <div className={"subtext"}>
                      {(() => {
                        if (this.props.data.speakAs) {
                          const sel = window.apm.knowledgeBase.getIndexFromValue(
                            this.props.data.speakAs,
                            speakAsOptions
                          );
                          if (sel >= 0) {
                            return (
                              <span>
                                {
                                  window.l.form.speak_as_colon + speakAsOptions[sel].name
                                }
                              </span>
                            );
                          }
                        }
                        return <span>{window.l.form.no_speak_as_defined}</span>;
                      })()}
                    </div>
                  </td>
                  <td className={"icon"}>
                    <i className={"fe icon-edit-3"} />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {(() => {
            if (this.state.showJsx) {
              return (
                <Submenu
                  content={DynamicVariableSubCon}
                  context={this.props.context}
                  domContext={this}
                  data={{ varIdx: this.props.varIdx, input: this.props.data }}
                />
              );
            }
            return <span />;
          })()}
        </div>
      </li>
    );
  }
}

/**
 * @class LocalIntentItem
 */
class LocalIntentItem extends ListItem {
  checkError = () => {
    const { data, context} = this.props
    if(context.props.context.getUnusedIntents){
      const unusedIntents = context.props.context.getUnusedIntents();
      return unusedIntents && unusedIntents.includes(data);
    }

    return false
  }

  openList() {
    if(!this.props.readOnly){
      this.setState({ showJsx: true });
    }
  }

  render() {
    const selection = window.apm.knowledgeBase.getIntentIndexFromValue(
      this.props.data
    );
    if (selection < 0) {
      return null;
    }
    const optionName = window.apm.knowledgeBase.getIntents()[selection].name;
    const hasError = this.checkError();
    return (
      <li style={{ marginBottom: '4px'}}>
        <div
          style={{ width: "auto", marginLeft: "-12px", marginRight: "-12px" }}
        >
          <button
          style={hasError ? { boxShadow: "0 0 4px -2px var(--theme-error)", outline: "1px solid var(--theme-error)", opacity: "0.5" } : undefined}
            className="form-list-item item-button"
            onClick={() => this.openList()}
          >
            <table>
              <tbody>
                <tr>
                  <td>
                    <div>{optionName}</div>
                  </td>
                  <td className="icon">
                    <i className="fe icon-edit-3" />
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
          {this.state.showJsx && (
                <Submenu
                  content={this.props.content}
                  context={this.props.context}
                  domContext={this}
                  data={{ varIdx: this.props.varIdx, intent: this.props.data }}
                  readOnly={this.props.readOnly}
                />
              )
            
          }
        </div>
      </li>
    );
  }
}

/**
 * @class KeyValueItem
 */
class KeyValueItem extends ListItem {
  constructor(props) {
    super(props);
    
    this.defaultKeyView = (this.props.updateOnly)
      ? { 
          type: "Label", 
          key: "key"
        }
      : { 
          type: "Input", 
          key: "key" , 
          suggestion: "variable",  
          autoCompleteConversationVariable : this.props.context.props.label === "Conversational Variables",
          keyType: this.props.context.props.keyType
        };
    
        this.defaultValueView = { type: "Input", key: "value", suggestion: "variable" };
  }

  setConfig(_, value) {
    this.props.context.update(value, this.props.varIdx);
  }

  remove() {
    this.props.context.remove(this.props.varIdx);
  }

  render() {
    
    return (
      <li>
        {
          <KeyValue
            context={this}
            valKey={this.props.data.key}
            value={this.props.data}
            icon={this.props.icon}
            allowDelete={this.props.allowDelete}
            pairKeyView={
              this.props.pairKeyView
                ? this.props.pairKeyView
                : this.defaultKeyView
            }
            pairValueView={
              this.props.pairValueView
                ? this.props.pairValueView
                : this.defaultValueView
            }
          />
        }
      </li>
    );

  }
}

/**
 * @class SchemaItem
 */
class SchemaItem extends ListItem {
  constructor(props) {
    super(props);
  }

  getConfig(key) {
    return this.props.data[key];
  }

  setConfig(_, value) {
    this.props.context.update(value, this.props.varIdx);
  }
  
  remove() {
    this.props.context.remove(this.props.varIdx);
  }

  clickCallback() {
    const deleteButton = this.props.data.isCommonModule ? 'This is read-only data imported with '+this.props.data.moduleApp : (
      `<button class="form-button normal delete-button">${window.l.popup.delete_schema}</button>`
    );
    const saveButton = this.props.data.isCommonModule ? '': '<button class="form-button add-button">' +
    window.l.popup.save +
    "</button> ";
    window.apm.popup(
      window.l.popup.schema,
      '<div class="custom_script_container"></div>',
      deleteButton +
      '<button class="form-button normal" onclick="w2popup.close();">' +
      `  ${window.l.popup.close}` +
      "</button> " +
      saveButton,
      [
        ".custom_script_container",

        () => {
          ReactDOM.render(
            <CustomSchemaDialog context={this} new={false} />,
            $(".custom_script_container").get(0)
          );
          $(".w2ui-popup")
            .find(".add-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-add"));
            });
          $(".w2ui-popup")
            .find(".delete-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-delete"));
            });
        
        }
      ]
      
    );
  }

  render() {
    return (
      <li>
        <div style={{ width: "auto" }}>
          <button
            style={{ padding: "4px" }}
            className={"form-list-item item-button"}
          >
            <table>
              <tbody>
                <tr style={{ verticalAlign: "top" }}>
                  <td style={{ paddingRight: "8px" }}>
                    <div
                      style={{
                        gap: '.5em',
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <i className="fe icon-graphql" style={{ color: '#E535AB' }} />
                      {this.props.data.name}
                      {this.props.data.isCommonModule && (
                        <div className="comoLabel">
                          <i className="fe icon-globe" />
                          {this.props.data.moduleApp}
                        </div>
                      )}
                    </div>
                    <div className={"subtext"}>
                      <table
                        style={{ marginBottom: 0 }}
                        className={"summary-table table table-condensed"}
                      >
                        <tbody>
                          <tr>
                            {this.props.data.type !== "SCHEMA" && (
                              <td>{this.props.data.value}</td>
                            )}
                            {this.props.data.type === "SCHEMA" && (
                              <td>{this.props.data.schema}</td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                  <td className={"icon"}>
                    <button
                      style={{ padding: "4px" }}
                      className={"form-list-item item-button"}
                      onClick={() => {
                        this.clickCallback();
                      }}
                    >
                      <i className={"fe icon-edit-3"} />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
        </div>
      </li>
    );
  }
}

/**
 * @class CSVItem
 */
class CSVItem extends SchemaItem {
  constructor(props) {
    super(props);
  }

  update(_, value) {
      this.props.context.update(value, this.props.varIdx);
    }

  clickCallback() {
    const deleteButton = this.props.data.isCommonModule ? 'This is read-only data imported with '+this.props.data.moduleApp : (
      `<button class="form-button normal delete-button">${window.l.popup.delete_csv}</button>`
    );
    const saveButton = this.props.data.isCommonModule ? '': '<button class="form-button add-button">' +
    window.l.popup.update_csv +
    "</button> ";
    window.apm.popup(
      window.l.settings.csv_data,
      '<div class="custom_script_container"></div>',
      deleteButton +
      saveButton +
      '<button class="form-button normal" onclick="w2popup.close();">' +
      window.l.popup.close +
      "</button> ",
      [
        ".custom_script_container",

        () => {
          ReactDOM.render(
            <CustomCSVDialog context={this} new={false} reupload={true} />,
            $(".custom_script_container").get(0)
          );
          $(".w2ui-popup")
            .find(".delete-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-delete"));
            });
          $(".w2ui-popup")
            .find(".add-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-add"));
            });
        
        }
      ]
      
    );
  }

  render() {
    const csv = Papa.parse(this.props.data.value, {
      header: true
    });
    const header = csv.meta.fields;
    var headerRow = [];
    var rows = [];
    header.forEach((item, i) => {
      headerRow.push(<th key={i}>{item}</th>);
    });
    const len = Math.min(3, csv.data.length);
    for (var i = 0; i < len; i++) {
      if (Object.keys(csv.data[i]).length <= 1) {
        continue;
      }
      var r = [];
      header.forEach((item, j) => {
        r.push(<td key={i + "-" + j}>{csv.data[i][item]}</td>);
      });
      rows.push(<tr key={i}>{r}</tr>);
    }
    var footer = null;
    if (rows.length === 3 && csv.data.length > 3) {
      footer = (
        <td>
          <span>
            &#8943;{" "}
            {window.l.form.and_space +
              (csv.data.length - 3) +
              window.l.form.space_more_rows}
          </span>
        </td>
      );
    }
    return (
      <li>
        <div style={{ width: "auto" }}>
          <button
            style={{ padding: "4px" }}
            className={"form-list-item item-button"}
          >
            <table>
              <tbody>
                <tr style={{ verticalAlign: "top" }}>
                  <td style={{ paddingRight: "8px" }}>
                    <div style={{
                      gap: '.5em',
                      marginBottom: "4px",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <i className={"fe icon-grid"} />
                      {this.props.data.name}
                      {this.props.data.isCommonModule && (
                        <div className="comoLabel">
                          <i className="fe icon-globe" />
                          {this.props.data.moduleApp}
                        </div>
                      )}
                    </div>
                    <div className={"subtext"}>
                      <table
                        style={{ marginBottom: 0 }}
                        className={"summary-table table table-condensed"}
                      >
                        <thead>
                          <tr>{headerRow}</tr>
                        </thead>
                        <tbody>{rows}</tbody>
                        <tfoot>
                          <tr>{footer}</tr>
                        </tfoot>
                      </table>
                    </div>
                  </td>
                  <td className={"icon"}>
                    <button
                      style={{ padding: "4px" }}
                      className={"form-list-item item-button"}
                      onClick={() => {
                        this.clickCallback();
                      }}
                    >
                      <i className={"fe icon-edit-3"} />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
        </div>
      </li>
    );
  }
}

/**
 * @class FunctionItem
 */
class FunctionItem extends SchemaItem {
  constructor(props) {
    super(props);
  }

  getConfig(key) {
    return this.props.data[key];
  }

  clickCallback() {
    window.apm.popup(
      window.l.popup.function,
      '<div class="custom_script_container"></div>',
      '<button class="form-button normal delete-button">' +
      `  ${window.l.popup.delete_function}` +
      "</button> " +
      '<button class="form-button normal used-in-functions">' +
      `  ${window.l.popup.used_in}` +
      "</button> " +
      '<button class="form-button normal" onclick="w2popup.close();">' +
      `  ${window.l.popup.close}` +
      "</button> " +
      '<button class="form-button add-button">' +
      `  ${window.l.popup.save}` +
      "</button> ",
      [
        ".custom_script_container",
        
        () => {
          ReactDOM.render(
            <CustomFunctionDialog
              customFunction={this.props.data}
              onSave={(functionData) => this.setConfig("script", functionData)}
              onDelete={() => this.remove()}
            />,
            $(".custom_script_container").get(0)
          );

          $(".w2ui-popup")
            .find(".add-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-add"));
            });
          $(".w2ui-popup")
            .find(".delete-button")
            .on("click", function () {
              document.dispatchEvent(new CustomEvent("ev-dialog-delete"));
            });
          $(".w2ui-popup")
            .find(".used-in-functions")
            .on("click", () => this.usedInFunctions());
        }
      ]
    );
  }

  usedInFunctions() {
    const data = this.props.data;
    const name = this.props.data.name;

    window.apm.popup('Function: ' + name,
      '<div class="used_in_functions"></div>',
      '<button class="form-button normal">' + window.l.popup.close + '</button> ',
      [
        ".used_in_functions",

        () => {
          ReactDOM.render(
            <UsedInDialog
              isComponentNode={false}
              serialized={window.apm.serialized}
              node={data} />, $(".used_in_functions").get(0));
          
          $(".w2ui-popup").find("button.form-button").on("click", function () {
            ReactDOM.unmountComponentAtNode($(".used_in_functions").get(0));
            w2popup.close();
          });

        }
      ]

    );
  }

  render() {
    var serializedParams = "";
    const params = map(this.props.data.parameters, p => {
      return p.key + (p.value ? " = " + p.value : "");
    });
    if (params.length) {
      serializedParams = params.join(", ");
    }
    const functionSummary = "function (" + serializedParams + ") { ... }";
    return (
      <li>
        <div style={{ width: "auto" }}>
          <button
            style={{ padding: "4px" }}           
            className={"form-list-item item-button"}
          >
            <table>
              <tbody>
                <tr style={{ verticalAlign: "top" }}>
                  <td style={{ paddingRight: "8px" }}>
                    <div style={{ marginBottom: "4px" }}>
                      <i
                        className={"fe icon-cpu"}
                        style={{ marginRight: "4px" }}
                      />
                      {this.props.data.name}
                    </div>
                    <div className={"subtext"}>
                      <table
                        style={{ marginBottom: 0 }}
                        className={"summary-table table table-condensed"}
                      >
                        <tbody>
                          <tr>
                            <td>{functionSummary}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                  <td className={"icon"}>
                    <button
                      style={{ padding: "4px" }}           
                      className={"form-list-item item-button"}
                      onClick={() => {
                        this.clickCallback();
                      }}
                    >
                      <i className={"fe icon-edit-3"} />
                    </button>
                  </td>
                  <td className={"icon"}>
                    <button
                      style={{ padding: "4px" }}           
                      className={"form-list-item item-button"}
                      onClick={() => {
                        this.usedInFunctions();
                      }}
                    >
                      <i className={"fe icon-eye"} />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </button>
        </div>
      </li>
    );
  }
}
